import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function NavDropdown({ label, items, testId }) {
  const nav_ = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testId}
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors outline-none"
      >
        {label} <ChevronDown size={14} strokeWidth={2} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-2xl border-slate-200 shadow-lg min-w-[240px]">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.to}
            data-testid={`nav-${it.to.replace(/\//g, "")}`}
            onClick={() => nav_(it.to)}
            className="rounded-xl cursor-pointer py-2.5 font-medium text-slate-700"
          >
            {it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NavItem = ({ to, label }) => (
  <NavLink
    to={to}
    data-testid={`nav-${to.replace(/\//g, "")}`}
    className={({ isActive }) =>
      `text-sm font-medium transition-colors ${isActive ? "text-brand-700" : "text-slate-700 hover:text-slate-900"}`
    }
  >
    {label}
  </NavLink>
);

function DesktopAuthActions({ user, logout, t }) {
  const nav_ = useNavigate();
  if (user && user !== false) {
    return (
      <>
        {user.role === "admin" && (
          <Link to="/admin" className="text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="nav-admin">Admin</Link>
        )}
        <Link to="/espace" className="text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="nav-dashboard">{t("nav_dashboard")}</Link>
        <Button data-testid="logout-btn" onClick={logout} variant="outline" className="rounded-full">{t("nav_logout")}</Button>
      </>
    );
  }
  return (
    <>
      <Link to="/connexion" className="text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="nav-login">{t("nav_login")}</Link>
      <button onClick={() => nav_("/inscription")} className="btn-primary text-sm" data-testid="nav-register">{t("nav_register")}</button>
    </>
  );
}

function MobileMenu({ links, user, logout, onClose }) {
  const connected = user && user !== false;
  return (
    <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
      <div className="container-x py-4 flex flex-col gap-3">
        {links.map((n) => (
          <Link key={n.to} to={n.to} onClick={onClose} className="py-2 text-slate-800 font-medium">
            {n.label}
          </Link>
        ))}
        {connected ? (
          <>
            {user.role === "admin" && (
              <Link to="/admin" onClick={onClose} className="py-2 text-slate-800 font-medium">Admin</Link>
            )}
            <Link to="/espace" onClick={onClose} className="py-2 text-slate-800 font-medium">Mon espace</Link>
            <button onClick={() => { logout(); onClose(); }} className="btn-outline text-sm">Déconnexion</button>
          </>
        ) : (
          <>
            <Link to="/connexion" onClick={onClose} className="py-2 text-slate-800 font-medium">Se connecter</Link>
            <Link to="/inscription" onClick={onClose} className="btn-primary text-sm">Créer un compte</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const resourceItems = [
    { to: "/blog", label: t("nav_blog") },
    { to: "/guides", label: t("nav_guides") },
  ];
  const mobileLinks = [
    { to: "/formations", label: t("nav_formations") },
    { to: "/offre", label: t("nav_offers") },
    ...resourceItems,
    { to: "/a-propos", label: t("nav_about") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-slate-200/60" data-testid="site-header">
      <div className="container-x flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <img src="/logo-beehive.png" alt="Beehive Academy" className="h-11 w-11 object-contain" />
          <span className="font-display font-bold text-lg tracking-tight text-slate-900">Beehive Enfance et Éducation Academy</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavItem to="/formations" label={t("nav_formations")} />
          <NavItem to="/offre" label={t("nav_offers")} />
          <NavDropdown label={t("nav_resources")} items={resourceItems} testId="nav-resources-trigger" />
          <NavItem to="/a-propos" label={t("nav_about")} />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <DesktopAuthActions user={user} logout={logout} t={t} />
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && <MobileMenu links={mobileLinks} user={user} logout={logout} onClose={() => setOpen(false)} />}
    </header>
  );
}
